import React, { ReactElement } from 'react'

import CallMovementFiltering from './CallMovementFiltering'
import DurationFiltering from './DurationFiltering'

import { RptFilterEntityInfo } from '../../Common/Types/Types'

// CSS imports. Commented out because of current build process.
// import './otherTab.scss'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    telnosMovedTo: string
    setTelnosMovedTo: (newValue: string) => void
    devicesMovedFrom: string
    setDevicesMovedFrom: (newValue: string) => void
    ACDAgentsMovedFrom: string
    setACDAgentsMovedFrom: (newValue: string) => void
    callSegmentNumber: number
    setCallSegmentNumber: (newValue: number) => void
    callSegmentStartReason: number
    setCallSegmentStartReason: (newValue: number) => void
    callSegmentEndReason: number
    setCallSegmentEndReason: (newValue: number) => void
    callSegmentChainID: string
    setCallSegmentChainID: (newValue: string) => void
    ringTimeDistComparisonType: number
    setRingTimeDistComparisonType: (newValue: number) => void
    ringTimeDistValue1: string
    setRingTimeDistValue1: (newValue: string) => void
    ringTimeDistValue2: string
    setRingTimeDistValue2: (newValue: string) => void
    ringTimeSegmentComparisonType: number
    setRingTimeSegmentComparisonType: (newValue: number) => void
    ringTimeSegmentValue1: string
    setRingTimeSegmentValue1: (newValue: string) => void
    ringTimeSegmentValue2: string
    setRingTimeSegmentValue2: (newValue: string) => void
    callTalkTimeComparisonType: number
    setCallTalkTimeComparisonType: (newValue: number) => void
    callTalkTimeValue1: string
    setCallTalkTimeValue1: (newValue: string) => void
    callTalkTimeValue2: string
    setCallTalkTimeValue2: (newValue: string) => void
    totalCallTimeComparisonType: number
    setTotalCallTimeComparisonType: (newValue: number) => void
    totalCallTimeValue1: string
    setTotalCallTimeValue1: (newValue: string) => void
    totalCallTimeValue2: string
    setTotalCallTimeValue2: (newValue: string) => void
    callHeldTimeComparisonType: number
    setCallHeldTimeComparisonType: (newValue: number) => void
    callHeldTimeValue1: string
    setCallHeldTimeValue1: (newValue: string) => void
    callHeldTimeValue2: string
    setCallHeldTimeValue2: (newValue: string) => void
    filterDurationRingSupported: boolean
    filterDurationsOtherSupported: boolean
    isReportTemplateUser: boolean
    currentTelSysType?: number
}

/**
 * The "Other" tab of report configuration modal window.
 *
 * @param filterEntityInfo The list of entities according to the filter.
 * @param telnosMovedTo Report property.
 * @param setTelnosMovedTo Setter for corresponding report property.
 * @param devicesMovedFrom Report property.
 * @param setDevicesMovedFrom Setter for corresponding report property.
 * @param ACDAgentsMovedFrom Report property.
 * @param setACDAgentsMovedFrom Setter for corresponding report property.
 * @param callSegmentNumber Report property.
 * @param setCallSegmentNumber Setter for corresponding report property.
 * @param callSegmentStartReason Report property.
 * @param setCallSegmentStartReason Setter for corresponding report property.
 * @param callSegmentEndReason Report property.
 * @param setCallSegmentEndReason Setter for corresponding report property.
 * @param callSegmentChainID Report property.
 * @param setCallSegmentChainID Setter for corresponding report property.
 * @param ringTimeDistComparisonType Report property.
 * @param setRingTimeDistComparisonType Setter for corresponding report property.
 * @param ringTimeDistValue1 Report property.
 * @param setRingTimeDistValue1 Setter for corresponding report property.
 * @param ringTimeDistValue2 Report property.
 * @param setRingTimeDistValue2 Setter for corresponding report property.
 * @param ringTimeSegmentComparisonType Report property.
 * @param setRingTimeSegmentComparisonType Setter for corresponding report property.
 * @param ringTimeSegmentValue1 Report property.
 * @param setRingTimeSegmentValue1 Setter for corresponding report property.
 * @param ringTimeSegmentValue2 Report property.
 * @param setRingTimeSegmentValue2 Setter for corresponding report property.
 * @param callTalkTimeComparisonType Report property.
 * @param setCallTalkTimeComparisonType Setter for corresponding report property.
 * @param callTalkTimeValue1 Report property.
 * @param setCallTalkTimeValue1 Setter for corresponding report property.
 * @param callTalkTimeValue2 Report property.
 * @param setCallTalkTimeValue2 Setter for corresponding report property.
 * @param totalCallTimeComparisonType Report property.
 * @param setTotalCallTimeComparisonType Setter for corresponding report property.
 * @param totalCallTimeValue1 Report property.
 * @param setTotalCallTimeValue1 Setter for corresponding report property.
 * @param totalCallTimeValue2 Report property.
 * @param setTotalCallTimeValue2 Setter for corresponding report property.
 * @param callHeldTimeComparisonType Report property.
 * @param setCallHeldTimeComparisonType Setter for corresponding report property.
 * @param callHeldTimeValue1 Report property.
 * @param setCallHeldTimeValue1 Setter for corresponding report property.
 * @param callHeldTimeValue2 Report property.
 * @param setCallHeldTimeValue2 Setter for corresponding report property.
 * @param displayDurationFiltering Whether the duration filtering section should be displayed.
 * @param filterDurationRingSupported Report definition property.
 * @param filterDurationsOtherSupported Report definition property.
 * @param isReportTemplateUser Whether the current user is a report template user.
 */
const OtherTab: React.FC<Props> = ({
    filterEntityInfo,
    telnosMovedTo,
    setTelnosMovedTo,
    devicesMovedFrom,
    setDevicesMovedFrom,
    ACDAgentsMovedFrom,
    setACDAgentsMovedFrom,
    callSegmentNumber,
    setCallSegmentNumber,
    callSegmentStartReason,
    setCallSegmentStartReason,
    callSegmentEndReason,
    setCallSegmentEndReason,
    callSegmentChainID,
    setCallSegmentChainID,
    ringTimeDistComparisonType,
    setRingTimeDistComparisonType,
    ringTimeDistValue1,
    setRingTimeDistValue1,
    ringTimeDistValue2,
    setRingTimeDistValue2,
    ringTimeSegmentComparisonType,
    setRingTimeSegmentComparisonType,
    ringTimeSegmentValue1,
    setRingTimeSegmentValue1,
    ringTimeSegmentValue2,
    setRingTimeSegmentValue2,
    callTalkTimeComparisonType,
    setCallTalkTimeComparisonType,
    callTalkTimeValue1,
    setCallTalkTimeValue1,
    callTalkTimeValue2,
    setCallTalkTimeValue2,
    totalCallTimeComparisonType,
    setTotalCallTimeComparisonType,
    totalCallTimeValue1,
    setTotalCallTimeValue1,
    totalCallTimeValue2,
    setTotalCallTimeValue2,
    callHeldTimeComparisonType,
    setCallHeldTimeComparisonType,
    callHeldTimeValue1,
    setCallHeldTimeValue1,
    callHeldTimeValue2,
    setCallHeldTimeValue2,
    filterDurationRingSupported,
    filterDurationsOtherSupported,
    isReportTemplateUser,
    currentTelSysType
}: Props): ReactElement => (
    <div className='other-tab tab-area'>
        <CallMovementFiltering
            filterEntityInfo={filterEntityInfo}
            telnosMovedTo={telnosMovedTo}
            setTelnosMovedTo={setTelnosMovedTo}
            devicesMovedFrom={devicesMovedFrom}
            setDevicesMovedFrom={setDevicesMovedFrom}
            ACDAgentsMovedFrom={ACDAgentsMovedFrom}
            setACDAgentsMovedFrom={setACDAgentsMovedFrom}
            callSegmentNumber={callSegmentNumber}
            setCallSegmentNumber={setCallSegmentNumber}
            callSegmentStartReason={callSegmentStartReason}
            setCallSegmentStartReason={setCallSegmentStartReason}
            callSegmentEndReason={callSegmentEndReason}
            setCallSegmentEndReason={setCallSegmentEndReason}
            callSegmentChainID={callSegmentChainID}
            setCallSegmentChainID={setCallSegmentChainID}
            isReportTemplateUser={isReportTemplateUser}
            currentTelSysType={currentTelSysType}
        />
        {(filterDurationRingSupported || filterDurationsOtherSupported) && (
            <DurationFiltering
                ringTimeDistComparisonType={ringTimeDistComparisonType}
                setRingTimeDistComparisonType={setRingTimeDistComparisonType}
                ringTimeDistValue1={ringTimeDistValue1}
                setRingTimeDistValue1={setRingTimeDistValue1}
                ringTimeDistValue2={ringTimeDistValue2}
                setRingTimeDistValue2={setRingTimeDistValue2}
                ringTimeSegmentComparisonType={ringTimeSegmentComparisonType}
                setRingTimeSegmentComparisonType={setRingTimeSegmentComparisonType}
                ringTimeSegmentValue1={ringTimeSegmentValue1}
                setRingTimeSegmentValue1={setRingTimeSegmentValue1}
                ringTimeSegmentValue2={ringTimeSegmentValue2}
                setRingTimeSegmentValue2={setRingTimeSegmentValue2}
                callTalkTimeComparisonType={callTalkTimeComparisonType}
                setCallTalkTimeComparisonType={setCallTalkTimeComparisonType}
                callTalkTimeValue1={callTalkTimeValue1}
                setCallTalkTimeValue1={setCallTalkTimeValue1}
                callTalkTimeValue2={callTalkTimeValue2}
                setCallTalkTimeValue2={setCallTalkTimeValue2}
                totalCallTimeComparisonType={totalCallTimeComparisonType}
                setTotalCallTimeComparisonType={setTotalCallTimeComparisonType}
                totalCallTimeValue1={totalCallTimeValue1}
                setTotalCallTimeValue1={setTotalCallTimeValue1}
                totalCallTimeValue2={totalCallTimeValue2}
                setTotalCallTimeValue2={setTotalCallTimeValue2}
                callHeldTimeComparisonType={callHeldTimeComparisonType}
                setCallHeldTimeComparisonType={setCallHeldTimeComparisonType}
                callHeldTimeValue1={callHeldTimeValue1}
                setCallHeldTimeValue1={setCallHeldTimeValue1}
                callHeldTimeValue2={callHeldTimeValue2}
                setCallHeldTimeValue2={setCallHeldTimeValue2}
                filterDurationsOtherSupported={filterDurationsOtherSupported}
                filterDurationRingSupported={filterDurationRingSupported}
            />
        )}
    </div>
)

export default OtherTab
